// Anderson College navbar logo.
//
// 2026-06-01: Replaced the traced inline SVG with the official brand-issued
// horizontal PNGs supplied by the client (COLOR + WHITE LINE LOGO Finals)
// + multi-density srcset so the navbar logo renders crisply on every DPR.
// The colour logo renders when the navbar is on a light surface (variant
// "dark"); the white logo renders on the dark hero/transparent navbar
// (variant "light"). A short opacity crossfade preserves the same scroll
// transition behaviour the inline-SVG version had.
//
// Source aspect ratio: 1810/241 ≈ 7.51:1 — within 3% of the previous
// 1500/194 inline SVG, so on-page footprint is visually unchanged.
//
// Why plain <img> and not SmartImg/<picture>?  Production CDN serves .webp
// with `application/octet-stream` (open ticket with Emergent Support). The
// <picture><source> selection breaks silently on that MIME for some browsers.
// Plain <img> + srcset decodes from magic bytes regardless of content-type,
// so this is the bulletproof path for the most-viewed asset on every page.

const COLOR_BASE = "/assets/brand/logo-horizontal-color";  // @1x.png + @2x.png
const WHITE_BASE = "/assets/brand/logo-horizontal-white";  // @1x.png + @2x.png + @3x.png

// Display container is height-bounded to 48% of the parent (the navbar
// bar height — typically 76 px desktop / 64 px mobile). Width auto-locks
// to the aspect ratio of the brand-issued PNG.
const Logo = ({ variant = "light", className = "" }) => {
  const showDark = variant === "dark";
  // Use multi-density srcset so the browser picks the right pixel-density
  // file for every device (1x desktop, 2x retina laptops, 3x phones).
  const src = `${showDark ? COLOR_BASE : WHITE_BASE}@1x.png`;
  const srcSet = showDark
    ? `${COLOR_BASE}@1x.png 1x, ${COLOR_BASE}@2x.png 2x`
    : `${WHITE_BASE}@1x.png 1x, ${WHITE_BASE}@2x.png 2x, ${WHITE_BASE}@3x.png 3x`;
  return (
    <span
      className={`relative block h-[48%] max-h-full shrink-0 ${className}`}
      style={{ aspectRatio: "1810 / 241" }}
      data-testid="ac-logo-wrapper"
      role="img"
      aria-label="Anderson College of Health, Business and Technology"
    >
      <img
        src={src}
        srcSet={srcSet}
        alt="Anderson College of Health, Business and Technology"
        data-testid="ac-logo"
        className="absolute inset-0 w-full h-full object-contain"
        decoding="async"
        // Above-the-fold logo — eager load so the navbar never flashes empty.
        loading="eager"
        // Match the previous SVG's smooth scroll crossfade behaviour.
        style={{
          transition: "opacity 0.4s ease-in-out",
          // `image-rendering: auto` is the spec-compliant smooth resample —
          // some browsers default to `crisp-edges` for PNG, which made the
          // earlier single-density variant look chunky/blurry on retina.
          imageRendering: "auto",
        }}
        /* 2026-10: removed aria-hidden="true" + alt="". The <span>
           wrapper was role="img" aria-label=... which hid the inner
           <img> from screen readers but ALSO meant Google got no alt
           text on the logo. Now the <img> owns the accessible name
           directly and the wrapper's role="img" is still valid as a
           presentational grouping. */
      />
    </span>
  );
};

export default Logo;
